// Token Counter Service for Baba AI
// Estimates token count for Llama 3.1 405B model
// Used by Seamless Windows to trigger rollover at 95K tokens
//
// WHY 95K THRESHOLD:
// - Llama 3.1 405B context: 128K tokens (but Together AI may limit to 190K with extended context)
// - "Lost in the Middle" phenomenon: LLMs forget middle content as context grows
// - At 50% capacity, memory degradation starts
// - 95K = safe threshold before quality drops
//
// TOKEN ESTIMATION:
// - Llama uses BPE tokenizer similar to GPT
// - Average: ~4 characters per token for English
// - Dutch/mixed languages: ~3.5 characters per token (less efficient)
// - We use conservative estimate to avoid undercount

const THRESHOLD_TOKENS = 95000; // 50% of 190K - trigger rollover here
const CHARS_PER_TOKEN = 3.5;    // Conservative for multilingual (Dutch/English/Farsi)

/**
 * Estimate token count for a string
 * Uses character-based estimation (conservative)
 * 
 * @param {string} text - Text to estimate
 * @returns {number} Estimated token count
 */
export function estimateTokens(text) {
  if (!text || typeof text !== 'string') return 0;
  
  // Base estimation: characters / chars_per_token
  const charCount = text.length;
  let tokens = Math.ceil(charCount / CHARS_PER_TOKEN);
  
  // Adjust for special patterns that use more tokens:
  // - Code blocks (more tokens due to syntax)
  // - Numbers (each digit often = 1 token)
  // - Special characters
  
  const codeBlockMatches = text.match(/```[\s\S]*?```/g) || [];
  const codeBlockChars = codeBlockMatches.reduce((sum, block) => sum + block.length, 0);
  // Code uses ~2.5 chars per token (more tokens)
  tokens += Math.ceil(codeBlockChars * (1/2.5 - 1/CHARS_PER_TOKEN));
  
  // URLs use many tokens
  const urlMatches = text.match(/https?:\/\/[^\s]+/g) || [];
  const urlChars = urlMatches.reduce((sum, url) => sum + url.length, 0);
  tokens += Math.ceil(urlChars * 0.3); // URLs are ~30% more token-heavy
  
  return tokens;
}

/**
 * Count tokens in a message object
 * 
 * @param {Object} message - Message with role and content
 * @returns {number} Estimated token count
 */
export function countMessageTokens(message) {
  if (!message) return 0;
  
  let tokens = 0;
  
  // Role token (user/assistant/system)
  tokens += 4; // Role markers use ~4 tokens
  
  // Content
  tokens += estimateTokens(message.content || '');
  
  // File attachments (if present)
  if (message.files && Array.isArray(message.files)) {
    message.files.forEach(file => {
      tokens += estimateTokens(file.content || '');
      tokens += estimateTokens(file.name || '');
      tokens += 10; // File metadata overhead
    });
  }
  
  return tokens;
}

/**
 * Count total tokens in conversation
 * 
 * @param {Array} messages - Array of message objects
 * @returns {number} Total estimated token count
 */
export function countConversationTokens(messages) {
  if (!messages || !Array.isArray(messages)) return 0;
  
  let total = 0;
  
  // System prompt overhead (Baba AI personality, tool definitions, etc.)
  // This is sent with every request
  total += 2000; // Estimated system prompt size
  
  // Count each message
  messages.forEach(message => {
    total += countMessageTokens(message);
  });
  
  // Conversation structure overhead
  total += messages.length * 3; // Message separators
  
  return total;
}

/**
 * Check if conversation is approaching threshold
 * 
 * @param {Array} messages - Array of message objects
 * @returns {Object} Status object with details
 */
export function checkThreshold(messages) {
  const currentTokens = countConversationTokens(messages);
  const percentage = (currentTokens / THRESHOLD_TOKENS) * 100;
  const remaining = THRESHOLD_TOKENS - currentTokens;
  
  return {
    currentTokens,
    threshold: THRESHOLD_TOKENS,
    percentage: Math.round(percentage * 10) / 10, // 1 decimal
    remaining: Math.max(0, remaining),
    shouldRollover: currentTokens >= THRESHOLD_TOKENS,
    status: getStatus(percentage)
  };
}

/**
 * Get human-readable status based on percentage
 */
function getStatus(percentage) {
  if (percentage < 50) return 'healthy';      // Green - plenty of room
  if (percentage < 75) return 'moderate';     // Yellow - getting there
  if (percentage < 90) return 'warning';      // Orange - prepare rollover
  if (percentage < 100) return 'critical';    // Red - rollover imminent
  return 'exceeded';                          // Rollover NOW
}

/**
 * Estimate how many more messages fit before rollover
 * 
 * @param {Array} messages - Current messages
 * @param {number} avgMessageSize - Average message size in chars (default: 500)
 * @returns {number} Estimated messages remaining
 */
export function estimateRemainingMessages(messages, avgMessageSize = 500) {
  const { remaining } = checkThreshold(messages);
  const tokensPerMessage = Math.ceil(avgMessageSize / CHARS_PER_TOKEN) + 4; // +4 for role
  const messagesRemaining = Math.floor(remaining / tokensPerMessage);
  
  // Account for assistant responses (roughly equal size)
  return Math.floor(messagesRemaining / 2);
}

/**
 * Format token count for display
 * 
 * @param {number} tokens - Token count
 * @returns {string} Formatted string (e.g., "45.2K")
 */
export function formatTokenCount(tokens) {
  if (tokens < 1000) return tokens.toString();
  if (tokens < 10000) return (tokens / 1000).toFixed(1) + 'K';
  return Math.round(tokens / 1000) + 'K';
}

// Export constants for external use
export const TOKEN_THRESHOLD = THRESHOLD_TOKENS;
export const CHARS_PER_TOKEN_ESTIMATE = CHARS_PER_TOKEN;

// Default export for convenience
export default {
  estimateTokens,
  countMessageTokens,
  countConversationTokens,
  checkThreshold,
  estimateRemainingMessages,
  formatTokenCount,
  TOKEN_THRESHOLD,
  CHARS_PER_TOKEN_ESTIMATE
};
